import { Section, eType, SectionInitData } from "../Section";
import { GameWorld, eGameObject as eWorldObject } from "../../GameWorld";
import { Game, eTextureAsset, eSfxSprite, SfxSprites, eAudioAsset, rnd } from "../../../../Game";
import { RND } from "../../../../Utils/RND";
import { TIMER } from "../../../Gameplay";
import { Character, eDeathReason } from "../../../Player/Character";
import { SoundManager } from "../../../../Utils/SoundManager";



const enum eState {
    on,
    turningOff,
    off,
    turningOn
}



export class BridgeInitData extends SectionInitData {
    get minCloseTime(): number { return this._minCloseTime; }
    private _minCloseTime: number;

    get maxCloseTime(): number { return this._maxCloseTime; }
    private _maxCloseTime: number;

    get minOpenTime(): number { return this._minOpenTime; }
    private _minOpenTime: number;

    get maxOpenTime(): number { return this._maxOpenTime; }
    private _maxOpenTime: number;

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor(minCloseTime: number, maxCloseTime: number, minOpenTime: number, maxOpenTime: number) {
        super(eType.bridge);

        this._minCloseTime = minCloseTime;
        this._maxCloseTime = maxCloseTime;
        this._minOpenTime = minOpenTime;
        this._maxOpenTime = maxOpenTime;
    }
}



export class Bridge extends Section {
    /**
     * Vzadlenost, o kterou se most posune pri ponoreni
     */
    private static readonly SUBMERSION_DIS = 0.35;

    /**
     * Sirka okraje reky, na ktere hrac muze jeste stat i bez vysunuteho mostu
     */
    private static readonly RIVER_BORDER = 0.15;

    private static _WATER_SURFACE_MAT: pc.StandardMaterial;

    private _settings: BridgeInitData;

    private _waterSurface: pc.MeshInstance;
    private _waterSurfaceTexTrans: Float32Array;

    private _bridgeEnt: pc.Entity;
    private _bridgePos: pc.Vec3;

    private _state: eState;
    private _time: number;
    private _switchProgress: number;

    private static _UPDATE_FNC: (() => void)[];

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor() {
        super("bridge", eType.bridge, true);

        // Update funkce
        if (!Bridge._UPDATE_FNC) {
            Bridge._UPDATE_FNC = [
                this.updateOn,
                this.updateTurningOff,
                this.updateOff,
                this.updateTurningOn
            ];
        }

        // Pozice mostu
        this._bridgePos = new pc.Vec3();

        // Posun textury pro hladinu vody
        this._waterSurfaceTexTrans = new Float32Array([4, 1, 0, 0]);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    activate(z: number, initData: SectionInitData): Section {
        this._settings = initData as BridgeInitData;

        super.activate(z, initData);

        this._bridgePos.copy(this._bridgeEnt.getLocalPosition());

        return this;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    reset(): Section {
        // Nastavit most bud jako vysunuty nebo jako zasunuty
        if (rnd.randomBool()) {
            this._state = eState.on;
            this._time = TIMER.time + rnd.randomIntIn(this._settings.minOpenTime, this._settings.maxOpenTime);
            this._bridgePos.y = 0;
        }
        else {
            this._state = eState.off;
            this._time = TIMER.time + rnd.randomIntIn(this._settings.minCloseTime, this._settings.maxCloseTime);
            this._bridgePos.y = -Bridge.SUBMERSION_DIS;
        }

        this._bridgeEnt.setLocalPosition(this._bridgePos);

        // Audio
        this._bridgeEnt.sound.stop(eSfxSprite.waterSplash);

        return this;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    update(): void {
        // Posun textury pasu
        this._waterSurfaceTexTrans[2] += 0.05 * TIMER.deltaS;
        this._waterSurface.setParameter("texture_diffuseMapTransform", this._waterSurfaceTexTrans);

        Bridge._UPDATE_FNC[this._state].call(this);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private updateOn(): void {
        if (TIMER.time >= this._time) {
            // Zahajit zasunuti mostu            
            this._state = eState.turningOff;
            this._time = TIMER.time;
            this._switchProgress = 0;

            SoundManager.playSound(this._bridgeEnt, eSfxSprite.waterSplash);
        }
        else if (this._time - 1000 <= TIMER.time) {
            // Nahodne menit pozici mostu na ose Y
            this._bridgePos.x = GameWorld.PATH_X + GameWorld.PATH_WIDTH / 2 + rnd.randomIn(-0.015, 0.015);
            this._bridgePos.y = rnd.randomIn(-0.01, 0.01);

            this._bridgeEnt.setLocalPosition(this._bridgePos);
        }
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private updateTurningOff(): void {
        let progress = (TIMER.time - this._time) / 500;

        if (progress >= 1) {
            progress = 1;
            this._state = eState.off;
            this._time = TIMER.time + rnd.randomIntIn(this._settings.minCloseTime, this._settings.maxCloseTime);
        }

        this._bridgePos.y = -Bridge.SUBMERSION_DIS * progress;
        this._bridgeEnt.setLocalPosition(this._bridgePos);

        this._switchProgress = progress;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private updateOff(): void {
        if (TIMER.time >= this._time) {
            // Zahajit vysunuti mostu
            this._state = eState.turningOn;
            this._time = TIMER.time;
            this._switchProgress = 0;
        }
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private updateTurningOn(): void {
        let progress = (TIMER.time - this._time) / 500;

        if (progress >= 1) {
            progress = 1;
            this._state = eState.on;
            this._time = + TIMER.time + rnd.randomIntIn(this._settings.minOpenTime, this._settings.maxOpenTime);
        }

        this._bridgePos.y = -Bridge.SUBMERSION_DIS + Bridge.SUBMERSION_DIS * progress;
        this._bridgeEnt.setLocalPosition(this._bridgePos);

        this._switchProgress = progress;
    }

    //-------------------------------------------------------------------------
    /**
     * Pridani vodni hladiny
     */
    //-------------------------------------------------------------------------
    private addWaterSurface(): void {
        if (!Bridge._WATER_SURFACE_MAT) {
            let material = Bridge._WATER_SURFACE_MAT = new pc.StandardMaterial();
            material.diffuseMap = Game.APP.assets.find(eTextureAsset.water, pc.ASSET_TEXTURE).resource;
            material.diffuseMapTiling.set(4, 1);
            material.update();
        }

        let surface = new pc.Entity();

        surface.addComponent(pc.eCompomentType.model, {
            type: pc.eModelType.plane,
            material: Bridge._WATER_SURFACE_MAT,
            castShadows: false,
            castShadowsLightmap: false,
        });

        surface.setLocalScale(7, 1, 0.7);
        surface.setLocalPosition(0, -0.149, 0);

        this._root.addChild(surface);
        this._waterSurface = surface.model.meshInstances[0];
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    protected addObjectEntities(): void {
        let world = GameWorld.instance;

        // Koryto s rekou
        let e = world.getObjectNewEntity(eWorldObject.river);
        e.setLocalPosition(0, 0, 0);
        e.reparent(this._root);

        // Voda
        this.addWaterSurface();

        // Most
        this._bridgeEnt = world.getObjectNewEntity(eWorldObject.bridge);
        this._bridgeEnt.setLocalPosition(0, 0, 0);
        this._bridgeEnt.reparent(this._root);

        this._bridgePos.set(0, 0, 0);

        if (!this._bridgeEnt.sound) {
            SoundManager.createSoundComponent(this._bridgeEnt, {
                positional: true,
                distanceModel: pc.DISTANCE_LINEAR,
                maxDistance: 2
            });

            SoundManager.addSoundSlot(this._bridgeEnt, eSfxSprite.waterSplash, SfxSprites[eSfxSprite.waterSplash], Game.APP.assets.find(eAudioAsset.sfx, pc.eAssetType.audio).id);
        }
    }

    //-------------------------------------------------------------------------
    /**
     * 
     * @param plChar Overit kolize s postavou hrace
     */
    //-------------------------------------------------------------------------
    checkPlCollisions(plChar: Character): void {
        if (this._state == eState.on || plChar.hasShield)
            return;

        let plZ = plChar.position.z;

        if (plZ < this.top + Bridge.RIVER_BORDER || plZ > this.bottom - Bridge.RIVER_BORDER)
            return;

        if ((this._state == eState.off) ||
            (this._state == eState.turningOff && this._switchProgress > 0.5) ||
            (this._state == eState.turningOn && this._switchProgress < 0.5)) {
            plChar.kill(eDeathReason.drowning);
        }
    }
}